import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

type Scope = 'home' | 'intervention';

interface Slice {
  id: string | undefined;
  startTime: Date | null;
  elapsedTime: number;
  isRunning: boolean;
  intervals: number[]; // accumulated seconds
}

interface TimerSliceControls {
  id: string | undefined;
  setId: (v: string | undefined) => void;
  startTime: Date | null;
  setStartTime: (v: Date | null) => void;
  endTime: Date | null;
  elapsedTime: number;
  isRunning: boolean;
  setIsRunning: (running: boolean) => void; // start/pause
  resetTimer: () => void;
}

interface TimerContextType {
  getSlice: (scope: Scope) => TimerSliceControls;
}

// ---------- storage helpers ----------
const K = (scope: Scope) => ({
  id: `${scope}-id`,
  start: `${scope}-start-time`,
  elapsed: `${scope}-elapsed-time`,
  intervals: `${scope}-intervals`,
  running: `${scope}-is-running`,
});

function loadSlice(scope: Scope): Slice {
  const k = K(scope);
  const id = localStorage.getItem(k.id) || undefined;
  const startIso = localStorage.getItem(k.start);
  const startTime = startIso ? new Date(startIso) : null;
  const elapsedTime = parseFloat(localStorage.getItem(k.elapsed) || '0');
  let intervals: number[] = [];
  try {
    intervals = JSON.parse(localStorage.getItem(k.intervals) || '[]');
  } catch {
    intervals = [];
  }
  const isRunning = localStorage.getItem(k.running) === 'true';
  return { id, startTime, elapsedTime, isRunning, intervals };
}

function persistSlice(scope: Scope, s: Slice) {
  const k = K(scope);
  if (s.id != null) localStorage.setItem(k.id, s.id);
  else localStorage.removeItem(k.id);

  if (s.startTime) localStorage.setItem(k.start, s.startTime.toISOString());
  else localStorage.removeItem(k.start);

  localStorage.setItem(k.elapsed, String(s.elapsedTime));
  localStorage.setItem(k.intervals, JSON.stringify(s.intervals));
  localStorage.setItem(k.running, s.isRunning ? 'true' : 'false');
}

// ---------- context ----------
const TimerContext = createContext<TimerContextType | undefined>(undefined);

export const TimerProvider = ({ children }: { children: React.ReactNode }) => {
  const [home, setHome] = useState<Slice>(() => loadSlice('home'));
  const [intervention, setIntervention] = useState<Slice>(() => loadSlice('intervention'));

  // Persist whenever slices change
  useEffect(() => persistSlice('home', home), [home]);
  useEffect(() => persistSlice('intervention', intervention), [intervention]);

  // Single tick that updates BOTH timers if they’re running
  const tickRef = useRef<number | null>(null);
  useEffect(() => {
    function update(slice: Slice, setSlice: React.Dispatch<React.SetStateAction<Slice>>) {
      if (!slice.isRunning || !slice.startTime) return;
      const now = new Date();
      const diff = (now.getTime() - slice.startTime.getTime()) / 1000;
      const totalElapsed = slice.intervals.reduce((a, b) => a + b, 0) + diff;
      setSlice(prev => ({ ...prev, elapsedTime: totalElapsed }));
    }

    const onTick = () => {
      update(home, setHome);
      update(intervention, setIntervention);
    };

    const id = window.setInterval(onTick, 1000);
    tickRef.current = id;
    window.addEventListener('focus', onTick);
    document.addEventListener('visibilitychange', onTick);

    return () => {
      if (tickRef.current != null) clearInterval(tickRef.current);
      window.removeEventListener('focus', onTick);
      document.removeEventListener('visibilitychange', onTick);
    };
  }, [home, intervention]);

  // Build per-scope controls
  function makeControls(slice: Slice, setSlice: React.Dispatch<React.SetStateAction<Slice>>): TimerSliceControls {
    const endTime =
    slice.startTime && !slice.isRunning
      ? new Date(slice.startTime.getTime() + slice.intervals.reduce((a, b) => a + b, 0) * 1000)
      : null;

    const startTimer = () => {
      const now = new Date();
      setSlice(prev => ({
        ...prev,
        startTime: now,
        isRunning: true,
      }));
    };

    const pauseTimer = () => {
      setSlice(prev => {
        if (!prev.startTime) return prev;
        const now = new Date();
        const diff = (now.getTime() - prev.startTime.getTime()) / 1000;
        const intervals = [...prev.intervals, diff];
        const elapsedTime = intervals.reduce((a, b) => a + b, 0);
        return {
          ...prev,
          isRunning: false,
          intervals,
          elapsedTime,
        };
      });
    };

    const resetTimer = () => {
      setSlice({
        id: undefined,
        startTime: null,
        elapsedTime: 0,
        isRunning: false,
        intervals: [],
      });
    };

    return {
      id: slice.id,
      setId: (v) => setSlice(prev => ({ ...prev, id: v })),
      startTime: slice.startTime,
      setStartTime: (v) => setSlice(prev => ({ ...prev, startTime: v })),
      endTime,
      elapsedTime: slice.elapsedTime,
      isRunning: slice.isRunning,
      setIsRunning: (running) => (running ? startTimer() : pauseTimer()),
      resetTimer,
    };
  }

  const value = useMemo<TimerContextType>(() => {
    return {
      getSlice: (scope: Scope) =>
        scope === 'home'
          ? makeControls(home, setHome)
          : makeControls(intervention, setIntervention),
    };
  }, [home, intervention]);

  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
};

// Hook that chooses which slice you want.
// Default to 'intervention' so existing code keeps working.
export function useTimer(scope: Scope = 'intervention'): TimerSliceControls {
  const ctx = useContext(TimerContext);
  if (!ctx) throw new Error('useTimer must be used within a TimerProvider');
  return ctx.getSlice(scope);
}
